"use client";
import { motion } from "framer-motion";
import { headerVariants, microHoverVariants } from "@/lib/animations";
import Link from "next/link";
import Image from "@/components/ui/MediaImage";
import { transformMediaUrl } from "@/lib/media-url-transformer";

export type YachtAboutCtaLink =
    | {
          type?: "page" | "custom";
          id?: number;
          slug?: string | null;
          title?: string | null;
          url?: string | null;
      }
    | null
    | undefined;

export interface YachtAboutCTAProps {
    subTitle?: string;
    title?: string;
    description?: string;
    feature1Title?: string;
    feature1Description?: string;
    feature2Title?: string;
    feature2Description?: string;
    image?: number | string | { mediaId?: number; url?: string; alt?: string } | null;
    ctaButtonText?: string;
    /** Kırmızı buton üzerindeki metin (varsayılan: Book Now!) */
    ctaLinkText?: string;
    ctaLink?: YachtAboutCtaLink;
    stat1Value?: string;
    stat1Title?: string;
    stat2Value?: string;
    stat2Title?: string;
    stat3Value?: string;
    stat3Title?: string;
}

const DEFAULT_IMAGE =
    "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=1200&fit=crop";

function getImageUrl(
    imageProp: YachtAboutCTAProps["image"],
    fallback: string
): string {
    if (!imageProp) return fallback;
    if (typeof imageProp === "object" && imageProp !== null && "url" in imageProp && imageProp.url) {
        return transformMediaUrl(imageProp.url, fallback) || imageProp.url;
    }
    if (typeof imageProp === "string") {
        return transformMediaUrl(imageProp, fallback) || imageProp;
    }
    return fallback;
}

function resolveCtaHref(link: YachtAboutCtaLink): string {
    if (!link || typeof link !== "object") return "#";
    if (link.type === "custom" && link.url) {
        const u = String(link.url).trim();
        return u || "#";
    }
    const slug = link.slug != null ? String(link.slug).trim() : "";
    if (slug) {
        const clean = slug.replace(/^\/+/, "");
        return clean ? `/${clean}` : "#";
    }
    return "#";
}

/** Sayfa editörü: tek &lt;a data-prop-path="ctaLink"&gt; — metin ctaLink.title; eski kayıtlar ctaLinkText */
function resolveCtaButtonLabel(
    link: YachtAboutCtaLink,
    legacyCtaLinkText: string | undefined,
    fallback: string
): string {
    const fromLink =
        link && typeof link === "object" && link.title != null && String(link.title).trim() !== ""
            ? String(link.title).trim()
            : "";
    if (fromLink) return fromLink;
    const leg = legacyCtaLinkText != null ? String(legacyCtaLinkText).trim() : "";
    if (leg) return leg;
    return fallback;
}

export default function YachtAboutCTA({
    subTitle = "Why Choose Us",
    title = "Top Reasons to Choose Yachtera",
    description = "Id dignissim ac ultrices sed imperdiet id. Odio velit morbi cum a magna euismod. Commodo amet mauris quis at.",
    feature1Title = "Luxurious Fleet",
    feature1Description =
        "Purus quis semper elementum viverra tellus. Urna arcu pulvinar est sodales. Faucibus pulvinar.",
    feature2Title = "Experienced Team",
    feature2Description =
        "Purus quis semper elementum viverra tellus. Urna arcu pulvinar est sodales. Faucibus pulvinar.",
    image,
    ctaButtonText = "Embark on Your Dream Voyage Today!",
    ctaLinkText = "Book Now!",
    ctaLink,
    stat1Value = "25+",
    stat1Title = "Total Yacht Fleet",
    stat2Value = "60+",
    stat2Title = "Exclusive Destinations",
    stat3Value = "32+",
    stat3Title = "Years of Experience",
}: YachtAboutCTAProps = {}) {
    const imageUrl = getImageUrl(image, DEFAULT_IMAGE);
    const ctaHref = resolveCtaHref(ctaLink);
    const ctaRedButtonLabel = resolveCtaButtonLabel(ctaLink, ctaLinkText, "Book Now!");

    return (
        <section className="w-full min-w-0 max-w-full overflow-x-clip lg:w-8/12 px-4 sm:px-5 lg:px-0 mx-auto py-12 sm:py-16 md:py-20 lg:py-[80px]">
            <div className="grid grid-cols-12 gap-6 sm:gap-8 lg:gap-10 min-w-0">
                <div className="col-span-12 min-w-0 lg:col-span-5">
                    <div className="w-full min-w-0">
                        <div className="flex flex-col min-w-0">
                            <motion.div className="w-full flex flex-col gap-6 mb-10" variants={headerVariants}>
                                <motion.span
                                    className="font-display font-medium text-alsero-first text-2xl sm:text-3xl md:text-3xl lg:text-4xl"
                                    variants={microHoverVariants}
                                    initial="rest"
                                    whileHover="hover"
                                    data-prop-path="subTitle"
                                >
                                    {subTitle}
                                </motion.span>
                                <motion.h2
                                    className="font-bold text-alsero-text-dark text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight sm:leading-tight md:leading-snug"
                                    data-prop-path="title"
                                >
                                    {title}
                                </motion.h2>
                                <p
                                    className="text-alsero-text-gray text-base sm:text-lg md:text-xl leading-relaxed max-w-prose"
                                    data-prop-path="description"
                                >
                                    {description}
                                </p>
                            </motion.div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 mb-8 min-w-0">
                                <div className="flex flex-col gap-3 min-w-0">
                                    <span className="w-12 h-12 shrink-0 rounded-lg bg-alsero-first flex items-center justify-center text-white">
                                        01
                                    </span>
                                    <h2
                                        className="text-alsero-second text-xl sm:text-2xl break-words"
                                        data-prop-path="feature1Title"
                                    >
                                        {feature1Title}
                                    </h2>
                                    <p
                                        className="text-alsero-text-gray text-base sm:text-lg break-words"
                                        data-prop-path="feature1Description"
                                    >
                                        {feature1Description}
                                    </p>
                                </div>
                                <div className="flex flex-col gap-3 min-w-0">
                                    <span className="w-12 h-12 shrink-0 rounded-lg bg-alsero-first flex items-center justify-center text-white">
                                        02
                                    </span>
                                    <h2
                                        className="text-alsero-second text-xl sm:text-2xl break-words"
                                        data-prop-path="feature2Title"
                                    >
                                        {feature2Title}
                                    </h2>
                                    <p
                                        className="text-alsero-text-gray text-base sm:text-lg break-words"
                                        data-prop-path="feature2Description"
                                    >
                                        {feature2Description}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-span-12 min-w-0 lg:col-span-7">
                    <Image
                        src={imageUrl}
                        alt={title}
                        width={800}
                        height={570}
                        className="w-full max-w-full h-auto min-h-[200px] sm:min-h-[280px] object-cover rounded-tl-lg rounded-tr-lg"
                        sizes="(max-width: 1024px) 100vw, 58vw"
                        data-prop-path="image"
                        unoptimized={imageUrl.startsWith("/api/media/proxy/")}
                    />
                </div>
            </div>
            <div className="grid grid-cols-12 gap-6 sm:gap-8 lg:gap-10 min-w-0 mt-6 sm:mt-8 lg:mt-0">
                <div className="col-span-12 min-w-0 lg:col-span-5">
                    <div className="bg-alsero-dark-gray flex flex-col gap-4 sm:gap-5 justify-center items-stretch sm:items-center py-6 sm:py-8 px-4 sm:px-6 rounded-lg w-full max-w-full">
                        <h2
                            className="w-full max-w-full sm:max-w-[85%] md:w-6/12 text-lg sm:text-xl md:text-2xl text-white text-center break-words hyphens-auto"
                            data-prop-path="ctaButtonText"
                        >
                            {ctaButtonText}
                        </h2>
                        <Link
                            href={ctaHref}
                            className="bg-alsero-first text-white w-full max-w-full sm:max-w-[90%] md:w-8/12 text-center py-3.5 sm:py-5 px-3 rounded-lg text-sm sm:text-base break-words"
                            data-prop-path="ctaLink"
                        >
                            {ctaRedButtonLabel}
                        </Link>
                    </div>
                </div>
                <div className="col-span-12 min-w-0 lg:col-span-7">
                    <div className="relative h-full w-full min-w-0 flex flex-col gap-6 sm:gap-8 md:flex-row md:flex-wrap md:items-start md:justify-between lg:flex-nowrap lg:items-center lg:justify-between lg:gap-4 xl:gap-6 before:content-[''] before:absolute before:inset-x-0 before:-top-[20px] before:h-[56px] before:bg-top before:bg-repeat-x before:bg-[length:auto_56px] before-alsero-white-line-56">
                        <div className="flex flex-col min-w-0 flex-1 basis-[30%] md:max-w-[32%] lg:max-w-none">
                            <h2
                                className="text-4xl sm:text-5xl md:text-6xl lg:text-3xl xl:text-7xl 2xl:text-8xl text-alsero-second font-bold tabular-nums break-all sm:break-normal"
                                data-prop-path="stat1Value"
                            >
                                {stat1Value}
                            </h2>
                            <p
                                className="text-alsero-light-gray text-base sm:text-lg md:text-xl break-words"
                                data-prop-path="stat1Title"
                            >
                                {stat1Title}
                            </p>
                        </div>
                        <div className="flex flex-col min-w-0 flex-1 basis-[30%] md:max-w-[32%] lg:max-w-none">
                            <h2
                                className="text-4xl sm:text-5xl md:text-6xl lg:text-3xl xl:text-7xl 2xl:text-8xl text-alsero-second font-bold tabular-nums break-all sm:break-normal"
                                data-prop-path="stat2Value"
                            >
                                {stat2Value}
                            </h2>
                            <p
                                className="text-alsero-light-gray text-base sm:text-lg md:text-xl break-words"
                                data-prop-path="stat2Title"
                            >
                                {stat2Title}
                            </p>
                        </div>
                        <div className="flex flex-col min-w-0 flex-1 basis-[30%] md:max-w-[32%] lg:max-w-none">
                            <h2
                                className="text-4xl sm:text-5xl md:text-6xl lg:text-3xl xl:text-7xl 2xl:text-8xl text-alsero-second font-bold tabular-nums break-all sm:break-normal"
                                data-prop-path="stat3Value"
                            >
                                {stat3Value}
                            </h2>
                            <p
                                className="text-alsero-light-gray text-base sm:text-lg md:text-xl break-words"
                                data-prop-path="stat3Title"
                            >
                                {stat3Title}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
